import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { AlertCircle, Check, X } from "lucide-react";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import { app } from "../firebase";
import { AdminStreamRequest, decideStreamRequest, listStreamRequests } from "../utils/streamRequests";

/** Admin only (the API refuses everyone else): approve or deny accounts' requests for a different number of streams. */
const StreamRequestsAdmin: React.FC = () => {
  const [user] = useAuthState(getAuth(app));
  const [pending, setPending] = useState<AdminStreamRequest[]>([]);
  const [decided, setDecided] = useState<AdminStreamRequest[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "denied" | "failed">("loading");
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [busyId, setBusyId] = useState<number | null>(null);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const result = await listStreamRequests(() => user.getIdToken());
      setPending(result.pending);
      setDecided(result.decided);
      setState("ready");
    } catch (error) {
      console.error(error);
      setState(String(error).includes("403") ? "denied" : "failed");
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const decide = async (request: AdminStreamRequest, approve: boolean) => {
    if (!user) return;
    setBusyId(request.id);
    setMessage("");
    const result = await decideStreamRequest(() => user.getIdToken(), request.id, approve, notes[request.id] ?? "");
    setBusyId(null);
    if (!result.ok) setMessage(result.message ?? "The decision could not be saved.");
    await load();
  };

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="streams" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Stream requests</h1>
              <p className="text-slate-500 mt-1 text-lg">
                Approving sets the account's limit to exactly the number it asked for. <Link to="/streams" className="text-blue-600 hover:underline">Back to streams</Link>
              </p>
            </div>

            {state === "denied" && <p className="text-slate-700">You don't have access to this page.</p>}
            {state === "failed" && (
              <div role="alert" className="p-4 rounded-2xl bg-red-50 text-red-700 border border-red-100 flex items-center gap-3 text-sm font-medium">
                <AlertCircle size={18} /> We couldn't load the requests. Refresh to try again.
              </div>
            )}
            {message && (
              <div role="alert" className="p-4 rounded-2xl bg-red-50 text-red-700 border border-red-100 text-sm font-medium">{message}</div>
            )}

            {state === "ready" && (
              <>
                <section className="space-y-4" aria-label="Waiting for a decision">
                  <h2 className="text-lg font-bold text-slate-900">Waiting ({pending.length})</h2>
                  {pending.length === 0 && <p className="text-slate-500">Nothing is waiting.</p>}
                  {pending.map((r) => (
                    <div key={r.id} className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-4">
                      <div>
                        <p className="font-semibold text-slate-900 break-all">{r.email}</p>
                        <p className="text-sm text-slate-500">
                          Wants <strong>{r.count}</strong> stream{r.count === 1 ? "" : "s"} · allowed {r.currentLimit}, using {r.used} · {new Date(r.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap break-words bg-slate-50 rounded-2xl p-4">{r.reason}</p>
                      <div>
                        <label htmlFor={`note-${r.id}`} className="block text-sm font-semibold text-slate-700 mb-2 ml-1">Note to the user (optional)</label>
                        <input
                          id={`note-${r.id}`}
                          value={notes[r.id] ?? ""}
                          onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                          className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-4 text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button onClick={() => decide(r, true)} disabled={busyId === r.id} className="btn-primary flex items-center gap-2 disabled:opacity-60">
                          <Check size={18} /> Approve {r.count}
                        </button>
                        <button onClick={() => decide(r, false)} disabled={busyId === r.id} className="btn-secondary flex items-center gap-2 disabled:opacity-60">
                          <X size={18} /> Deny
                        </button>
                      </div>
                    </div>
                  ))}
                </section>

                {decided.length > 0 && (
                  <section className="space-y-3" aria-label="Recently decided">
                    <h2 className="text-lg font-bold text-slate-900">Recently decided</h2>
                    <ul className="bg-white rounded-3xl shadow-sm border border-slate-100 divide-y divide-slate-100">
                      {decided.map((r) => (
                        <li key={r.id} className="p-4 text-sm">
                          <span className="font-medium text-slate-900 break-all">{r.email}</span>
                          <span className="text-slate-500"> · {r.count} stream{r.count === 1 ? "" : "s"} · </span>
                          <span className={r.status === "approved" ? "text-green-700 font-medium" : "text-slate-700 font-medium"}>{r.status}</span>
                          {r.note && <span className="text-slate-500"> · {r.note}</span>}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default StreamRequestsAdmin;
